const ZONES = [
  { from: 0, to: 25, color: '#ef4444' },
  { from: 25, to: 50, color: '#facc15' },
  { from: 50, to: 100, color: '#4ade80' },
];
const TICKS = [0, 25, 50, 75, 100];

// 270° gauge: 0% sits at bottom-left, 50% at top-center, 100% at bottom-right, leaving a 90° open
// gap centered at the bottom. Total sweep is 270°, so each percentage point is 2.7°.
const SWEEP_DEG = 270;
const START_ANGLE = 225; // angle (in our polar convention below) corresponding to 0%

const CX = 130;
const CY = 125;
const RADIUS = 88;
const NEEDLE_LENGTH = 70;

const percentToAngle = (pct) => START_ANGLE - (pct / 100) * SWEEP_DEG;

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy - r * Math.sin(angleRad) };
};

const describeArc = (cx, cy, r, startPct, endPct) => {
  const start = polarToCartesian(cx, cy, r, percentToAngle(startPct));
  const end = polarToCartesian(cx, cy, r, percentToAngle(endPct));
  const spanDeg = (endPct - startPct) * (SWEEP_DEG / 100);
  const largeArcFlag = spanDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

/** 270° speedometer-style gauge (red/yellow/green zones + needle) for a 0-100% achievement value.
 * No native recharts gauge exists, so this is a small hand-built SVG rather than pulling in a chart
 * library for one gauge. */
export default function RevenueGauge({ percentage = 0 }) {
  const clamped = Math.max(0, Math.min(100, percentage));
  const needleAngle = percentToAngle(clamped);
  const needleTip = polarToCartesian(CX, CY, NEEDLE_LENGTH, needleAngle);

  const needleBaseWidth = 12;
  const dx = needleTip.x - CX;
  const dy = needleTip.y - CY;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const perpX = (-dy / len) * needleBaseWidth;
  const perpY = (dx / len) * needleBaseWidth;
  const needleBaseA = { x: CX + perpX, y: CY + perpY };
  const needleBaseB = { x: CX - perpX, y: CY - perpY };

  return (
    <svg viewBox="0 0 260 230" width="100%" height="100%">
      {ZONES.map((zone) => (
        <path
          key={zone.color}
          d={describeArc(CX, CY, RADIUS, zone.from, zone.to)}
          fill="none"
          stroke={zone.color}
          strokeWidth={26}
          strokeLinecap="butt"
        />
      ))}

      {TICKS.map((pct) => {
        const labelPos = polarToCartesian(CX, CY, RADIUS + 26, percentToAngle(pct));
        return (
          <text
            key={pct}
            x={labelPos.x}
            y={labelPos.y}
            fontSize="12"
            fill="#6b7280"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {pct}%
          </text>
        );
      })}

      <polygon
        points={`${needleBaseA.x},${needleBaseA.y} ${needleTip.x},${needleTip.y} ${needleBaseB.x},${needleBaseB.y}`}
        fill="#000000"
      />
      <circle cx={CX} cy={CY} r={11} fill="#000000" />
    </svg>
  );
}
